import { IAutomationSchedule } from '@dadohr/shared/domainTypes/automations';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  IExperience,
  IExperienceTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  Schedule,
  ScheduleStatus,
  ScheduleType,
  isExperienceMessageLevelSchedule,
  isTaskLevelSchedule
} from '@dadohr/shared/domainTypes/schedules';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import { groupBy, sortBy } from 'lodash';
import { useCollection } from 'react-firebase-hooks/firestore';
import { ColoredChip } from '../../../../components/ColoredChip';
import { Loader } from '../../../../components/Loader';
import { FlexContainer, FlexItem } from '../../../../layout/Flex';
import { toAutomationScheduleDoc } from '../../../../services/automations';
import {
  getMessageTemplateById,
  getTaskTemplateForTaskId
} from '../../../../services/experiences/template';
import { store, useMappedLoadingValue } from '../../../../services/firebase';
import { toScheduleDoc } from '../../../../services/schedules/documents';
import { formatDatePrecise } from '../../../../services/time';
import { ChannelIcon } from '../Channels';
import { getItemDisplayPair } from '../../helpers/adminName';

export const getAllOpenSchedulesForInstance = (
  orgId: string,
  instanceId: string
) => {
  store()
    .collection(FS.schedules)
    .where('orgId', '==', orgId)
    .where('experienceId', '==', instanceId)
    .where('status', 'in', [ScheduleStatus.PENDING, ScheduleStatus.RETRY])
    .get()
    .then((s) => s.docs.map(toScheduleDoc));
};

const SchedulesByTypeContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing()
}));

const ScheduleListContainer = styled('div')((p) => ({
  marginLeft: p.theme.spacing(2)
}));

const ScheduleTypeChip = ({ type }: { type: ScheduleType }) => {
  switch (type) {
    case ScheduleType.EXPERIENCE_START:
      return (
        <ColoredChip size="medium" color="black" mode="light">
          Experience Start
        </ColoredChip>
      );
    case ScheduleType.EXPERIENCE_MESSAGE:
      return (
        <ColoredChip size="medium" color="purple" mode="light">
          Experience Message
        </ColoredChip>
      );
    case ScheduleType.TASK_START:
      return (
        <ColoredChip size="medium" color="blue" mode="light">
          Task Start
        </ColoredChip>
      );
    case ScheduleType.TASK_REMINDER:
      return (
        <ColoredChip size="medium" color="yellow" mode="light">
          Task Reminder
        </ColoredChip>
      );
    case ScheduleType.TASK_LATE_REMINDER:
      return (
        <ColoredChip size="medium" color="red" mode="light">
          Task Late Reminder
        </ColoredChip>
      );
    default:
      return null;
  }
};

const AutomationSchedules = ({
  tpl,
  ss
}: {
  tpl: Doc<IExperienceTemplate>;
  ss: Doc<IAutomationSchedule>[];
}) => {
  return (
    <SchedulesByTypeContainer>
      <ScheduleListContainer>
        {ss.map((s) => {
          const title =
            tpl.data.automations.find(
              (a) => a.id === s.data.automationTemplateId
            )?.title || '';
          return (
            <FlexContainer>
              <FlexItem flex={1}>{title}</FlexItem>
              <div>{formatDatePrecise(s.data.at)}</div>
            </FlexContainer>
          );
        })}
      </ScheduleListContainer>
    </SchedulesByTypeContainer>
  );
};

const SchedulesByType = ({
  tpl,
  exp,
  type,
  ss
}: {
  tpl: Doc<IExperienceTemplate>;
  exp: Doc<IExperience>;
  type: ScheduleType;
  ss: Doc<Schedule>[];
}) => {
  if (type === ScheduleType.TASK_END) {
    return null;
  }
  return (
    <SchedulesByTypeContainer>
      <div>
        <ScheduleTypeChip type={type} />
      </div>
      <ScheduleListContainer>
        {ss.map((s) => {
          const taskTpl = isTaskLevelSchedule(s)
            ? getTaskTemplateForTaskId(tpl, exp, s.data.taskId)
            : undefined;
          const msgTpl = isExperienceMessageLevelSchedule(s)
            ? getMessageTemplateById(tpl, s.data.msgId)
            : undefined;
          const tplItem = taskTpl ?? msgTpl;
          const { primary: rawPrimary, secondary: rawSecondary } = tplItem
            ? getItemDisplayPair(tplItem)
            : { primary: '', secondary: undefined };
          const replace = (s: string) =>
            s.replace(/\{\{\s*experience\.title\s*\}\}/g, tpl.data.title);
          const primary = replace(rawPrimary);
          const secondary = rawSecondary ? replace(rawSecondary) : undefined;
          return (
            <FlexContainer key={s.id}>
              {(isTaskLevelSchedule(s) ||
                isExperienceMessageLevelSchedule(s)) && (
                <>
                  <ChannelIcon channel={s.data.channel} size={16} />
                  <div>{s.data.actor}</div>
                  <div>|</div>
                  <FlexItem flex={1}>
                    <strong>{primary}</strong>
                    {secondary && <> <em>"{secondary}"</em></>}
                  </FlexItem>
                </>
              )}

              <div>{formatDatePrecise(s.data.at)}</div>
            </FlexContainer>
          );
        })}
      </ScheduleListContainer>
    </SchedulesByTypeContainer>
  );
};

export const UpcomingSchedules = ({
  tpl,
  exp
}: {
  tpl: Doc<IExperienceTemplate>;
  exp: Doc<IExperience>;
}) => {
  const [autScheds] = useMappedLoadingValue(
    useCollection(
      store()
        .collection(FS.automationSchedules)
        .where('orgId', '==', tpl.data.orgId)
        .where('experienceId', '==', exp.id)
        .where('status', 'in', [ScheduleStatus.PENDING, ScheduleStatus.RETRY])
    ),
    (s) =>
      sortBy(s.docs.map(toAutomationScheduleDoc), (x) => x.data.at.toMillis())
  );

  const [schedsByType] = useMappedLoadingValue(
    useCollection(
      store()
        .collection(FS.schedules)
        .where('orgId', '==', tpl.data.orgId)
        .where('experienceId', '==', exp.id)
        .where('status', 'in', [ScheduleStatus.PENDING, ScheduleStatus.RETRY])
    ),
    (s) => {
      const docs = s.docs.map(toScheduleDoc);
      const grouped = groupBy(docs, (d) => d.data.type);
      const asList = Object.entries(grouped).map(([k, v]) => ({
        type: k as ScheduleType,
        ss: sortBy(v, (s) => s.data.at.toMillis())
      }));
      return sortBy(asList, ({ type }) => {
        switch (type) {
          case ScheduleType.EXPERIENCE_START:
            return 0;
          case ScheduleType.EXPERIENCE_MESSAGE:
            return 1;
          case ScheduleType.TASK_START:
            return 2;
          case ScheduleType.TASK_REMINDER:
            return 3;
          case ScheduleType.TASK_LATE_REMINDER:
            return 4;
          default:
            return 5;
        }
      });
    }
  );

  return (
    <>
      {(!schedsByType || !autScheds) && <Loader height={400} />}
      {schedsByType && autScheds && (
        <>
          <h3>Experience Schedules</h3>
          {schedsByType.map((x) => (
            <SchedulesByType
              key={x.type}
              type={x.type}
              tpl={tpl}
              exp={exp}
              ss={x.ss}
            />
          ))}

          <h3>Automation Schedules</h3>
          <AutomationSchedules tpl={tpl} ss={autScheds} />
        </>
      )}
    </>
  );
};
